'use client'

import { useState } from 'react'
import {
  ChevronDown,
  Shield,
  Bot,
  FileText,
  Globe,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Link as LinkIcon,
  Lock,
} from 'lucide-react'
import {
  LenisFadeIn,
  LenisStaggered,
} from '@/components/animations/LenisReveal'
import Hero from '@/components/Hero'
import Link from 'next/link'

export default function AIPolicyPage() {
  const [openSections, setOpenSections] = useState<number[]>([0])

  const toggleSection = (index: number) => {
    setOpenSections(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    )
  }

  const sections = [
    {
      title: 'Introduction',
      icon: Shield,
      content: (
        <>
          <p>
            This AI Policy governs how Large Language Models (LLMs), AI systems,
            and automated crawlers may access, use, and interact with content on
            Pixelmojo&apos;s website (
            <a
              href='https://www.pixelmojo.io'
              className='text-primary hover:underline font-semibold'
            >
              pixelmojo.io
            </a>
            ). This policy is designed to balance open access for AI-assisted
            browsing while protecting our intellectual property from
            unauthorized training use.
          </p>
          <p>
            <strong>Last Updated:</strong> January 1, 2026
          </p>
          <p>
            <strong>Owner:</strong> Pixelmojo
          </p>
          <p>
            <strong>Contact:</strong>{' '}
            <a
              href='mailto:founders@pixelmojo.io'
              className='text-primary hover:underline'
            >
              founders@pixelmojo.io
            </a>
          </p>
        </>
      ),
    },
    {
      title: 'Allowed Uses',
      icon: CheckCircle2,
      content: (
        <>
          <p className='mb-4'>
            The following uses of our content are <strong>permitted</strong>:
          </p>
          <div className='space-y-4'>
            {[
              {
                title: 'Browsing and Citing',
                desc: 'AI systems may browse our content and cite it in AI-assisted answers with proper attribution.',
              },
              {
                title: 'Indexing for RAG/Semantic Search',
                desc: 'Content may be indexed for Retrieval-Augmented Generation (RAG) systems and semantic search with proper attribution.',
              },
              {
                title: 'Including in AI-Assisted Answers',
                desc: 'Our content may be included in AI-generated responses with source links and attribution.',
              },
              {
                title: 'Creating Summaries',
                desc: 'Summaries of our content may be created with attribution (maximum 75 words verbatim quotes).',
              },
            ].map((item, i) => (
              <div
                key={i}
                className='p-4 bg-muted/30 border border-line rounded-lg'
              >
                <h4 className='font-bold text-foreground mb-2 flex items-center gap-2'>
                  <CheckCircle2 className='h-5 w-5 text-green-500' />
                  {item.title}
                </h4>
                <p className='text-muted-foreground'>{item.desc}</p>
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      title: 'Prohibited Uses',
      icon: XCircle,
      content: (
        <>
          <p className='mb-4'>
            The following uses of our content are{' '}
            <strong>strictly prohibited</strong>:
          </p>
          <div className='space-y-4'>
            {[
              {
                title: 'Model Training',
                desc: 'Using our content for training datasets or model training purposes.',
              },
              {
                title: 'Fine-Tuning',
                desc: 'Fine-tuning models on our content without explicit written permission.',
              },
              {
                title: 'Verbatim Republishing',
                desc: 'Republishing our content verbatim without permission.',
              },
              {
                title: 'Commercial Redistribution',
                desc: 'Commercial redistribution or resale of our content.',
              },
              {
                title: 'Extended Cache Retention',
                desc: 'Retaining cached copies of our content beyond 30 days.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className='p-4 bg-destructive/10 border border-destructive/20 rounded-lg'
              >
                <h4 className='font-bold text-foreground mb-2 flex items-center gap-2'>
                  <XCircle className='h-5 w-5 text-destructive' />
                  {item.title}
                </h4>
                <p className='text-muted-foreground'>{item.desc}</p>
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      title: 'Attribution Requirements',
      icon: LinkIcon,
      content: (
        <>
          <p className='mb-4'>
            When citing or referencing our content, you must include proper
            attribution:
          </p>
          <div className='p-4 bg-muted/30 border border-line rounded-lg mb-4'>
            <code className='text-sm font-mono text-foreground'>
              Source: Pixelmojo (pixelmojo.io)
            </code>
          </div>
          <p className='mb-3'>
            <strong>Attribution Format:</strong>
          </p>
          <ul className='space-y-2 mb-4'>
            <li className='flex gap-3 items-start'>
              <span className='mt-2 h-1.5 w-1.5 bg-primary flex-shrink-0' />
              <span>
                Attribution is <strong>required</strong> for all uses
              </span>
            </li>
            <li className='flex gap-3 items-start'>
              <span className='mt-2 h-1.5 w-1.5 bg-primary flex-shrink-0' />
              <span>
                Include linkback to{' '}
                <a
                  href='https://www.pixelmojo.io'
                  className='text-primary hover:underline'
                >
                  https://www.pixelmojo.io
                </a>
              </span>
            </li>
            <li className='flex gap-3 items-start'>
              <span className='mt-2 h-1.5 w-1.5 bg-primary flex-shrink-0' />
              <span>
                Use the exact format: &quot;Source: Pixelmojo
                (pixelmojo.io)&quot;
              </span>
            </li>
          </ul>
          <p className='mb-3'>
            <strong>Derivatives Policy:</strong>
          </p>
          <ul className='space-y-2'>
            <li className='flex gap-3 items-start'>
              <span className='mt-2 h-1.5 w-1.5 bg-primary flex-shrink-0' />
              <span>
                Summaries are allowed with attribution (max 75 words verbatim)
              </span>
            </li>
            <li className='flex gap-3 items-start'>
              <span className='mt-2 h-1.5 w-1.5 bg-primary flex-shrink-0' />
              <span>Verbatim republishing is not permitted</span>
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'Rate Limiting & Cache Policy',
      icon: Clock,
      content: (
        <>
          <p className='mb-4'>
            To ensure fair use and system stability, we implement the following
            policies:
          </p>
          <div className='space-y-4'>
            <div className='p-4 bg-muted/30 border border-line rounded-lg'>
              <h4 className='font-bold text-foreground mb-2 flex items-center gap-2'>
                <Clock className='h-5 w-5 text-primary' />
                Rate Limiting
              </h4>
              <p className='text-muted-foreground mb-2'>
                Please limit requests to approximately{' '}
                <strong>1 request per second</strong>. We&apos;re burst-friendly
                but appreciate respectful crawling.
              </p>
              <p className='text-sm text-muted-foreground'>
                Rate limit: <strong>1 RPS</strong> (requests per second)
              </p>
            </div>
            <div className='p-4 bg-muted/30 border border-line rounded-lg'>
              <h4 className='font-bold text-foreground mb-2 flex items-center gap-2'>
                <Lock className='h-5 w-5 text-primary' />
                Cache Retention
              </h4>
              <p className='text-muted-foreground mb-2'>
                Cached copies of our content must not be retained beyond{' '}
                <strong>30 days</strong>.
              </p>
              <p className='text-sm text-muted-foreground'>
                Maximum cache retention: <strong>30 days</strong>
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      title: 'Allowed & Restricted Crawlers',
      icon: Bot,
      content: (
        <>
          <p className='mb-4'>
            We distinguish between AI browsing bots (allowed) and AI training
            bots (restricted):
          </p>
          <div className='grid md:grid-cols-2 gap-4 mb-6'>
            <div className='p-4 bg-green-500/10 border border-green-500/20 rounded-lg'>
              <h4 className='font-bold text-foreground mb-3 flex items-center gap-2'>
                <CheckCircle2 className='h-5 w-5 text-green-500' />
                Allowed Crawlers
              </h4>
              <p className='text-sm text-muted-foreground mb-3'>
                These bots may access our content for AI-assisted browsing:
              </p>
              <ul className='space-y-1'>
                {[
                  'GPTBot',
                  'ChatGPT-User',
                  'OAI-SearchBot',
                  'ClaudeBot',
                  'Claude-User',
                  'Claude-SearchBot',
                  'PerplexityBot',
                  'GoogleOther',
                ].map((bot, i) => (
                  <li key={i} className='text-sm flex items-center gap-2'>
                    <span className='h-1.5 w-1.5 bg-green-500 rounded-full' />
                    {bot}
                  </li>
                ))}
              </ul>
            </div>
            <div className='p-4 bg-destructive/10 border border-destructive/20 rounded-lg'>
              <h4 className='font-bold text-foreground mb-3 flex items-center gap-2'>
                <XCircle className='h-5 w-5 text-destructive' />
                Restricted Crawlers
              </h4>
              <p className='text-sm text-muted-foreground mb-3'>
                These bots are blocked from accessing our content:
              </p>
              <ul className='space-y-1'>
                {[
                  'CCBot',
                  'anthropic-ai',
                  'cohere-ai',
                  'Omgili',
                  'Omgilibot',
                ].map((bot, i) => (
                  <li key={i} className='text-sm flex items-center gap-2'>
                    <span className='h-1.5 w-1.5 bg-destructive rounded-full' />
                    {bot}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className='text-sm text-muted-foreground'>
            <strong>Note:</strong> Restricted crawlers may access{' '}
            <code className='bg-muted px-1 rounded'>/llms.txt</code> to
            understand our policy, but are blocked from accessing other content.
          </p>
        </>
      ),
    },
    {
      title: 'Allowed & Restricted Paths',
      icon: Globe,
      content: (
        <>
          <p className='mb-4'>
            The following paths define what content AI systems may access:
          </p>
          <div className='grid md:grid-cols-2 gap-4 mb-6'>
            <div className='p-4 bg-green-500/10 border border-green-500/20 rounded-lg'>
              <h4 className='font-bold text-foreground mb-3 flex items-center gap-2'>
                <CheckCircle2 className='h-5 w-5 text-green-500' />
                Allowed Sections
              </h4>
              <p className='text-sm text-muted-foreground mb-3'>
                AI browsing bots may access these sections:
              </p>
              <ul className='space-y-1'>
                {[
                  '/blogs/',
                  '/services/',
                  '/projects/',
                  '/about/',
                  '/pricing/',
                  '/llms.txt',
                ].map((path, i) => (
                  <li key={i} className='text-sm flex items-center gap-2'>
                    <span className='h-1.5 w-1.5 bg-green-500 rounded-full' />
                    <code className='bg-muted px-1 rounded'>{path}</code>
                  </li>
                ))}
              </ul>
            </div>
            <div className='p-4 bg-destructive/10 border border-destructive/20 rounded-lg'>
              <h4 className='font-bold text-foreground mb-3 flex items-center gap-2'>
                <XCircle className='h-5 w-5 text-destructive' />
                Restricted Paths
              </h4>
              <p className='text-sm text-muted-foreground mb-3'>
                These paths should not be crawled or indexed:
              </p>
              <ul className='space-y-1'>
                {[
                  '/api/*',
                  '/admin/*',
                  '/_next/*',
                  '/private/*',
                  '/*.json$',
                ].map((path, i) => (
                  <li key={i} className='text-sm flex items-center gap-2'>
                    <span className='h-1.5 w-1.5 bg-destructive rounded-full' />
                    <code className='bg-muted px-1 rounded'>{path}</code>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      title: 'Legal & Jurisdiction',
      icon: FileText,
      content: (
        <>
          <p className='mb-4'>
            <strong>License:</strong> All content © Pixelmojo. No data resale or
            redistribution permitted without explicit written permission.
          </p>
          <p className='mb-4'>
            <strong>Jurisdiction:</strong> This policy is governed by Philippine
            law and applicable international intellectual property treaties.
          </p>
          <p className='mb-4'>
            <strong>Content Ownership:</strong> All content on pixelmojo.io is
            proprietary to Pixelmojo. No data resale, redistribution, or
            training use is permitted without explicit written permission.
          </p>
          <div className='p-4 bg-muted/30 border border-line rounded-lg'>
            <h4 className='font-bold text-foreground mb-2'>
              Intellectual Property Rights
            </h4>
            <p className='text-sm text-muted-foreground'>
              Pixelmojo retains all intellectual property rights to content on
              this website. Unauthorized use, reproduction, or distribution of
              our content for training purposes is strictly prohibited and may
              result in legal action.
            </p>
          </div>
        </>
      ),
    },
    {
      title: 'Contact & Appeals',
      icon: AlertCircle,
      content: (
        <>
          <p className='mb-4'>
            For questions, concerns, or appeals regarding this AI Policy:
          </p>
          <div className='space-y-4'>
            <div className='p-4 bg-muted/30 border border-line rounded-lg'>
              <h4 className='font-bold text-foreground mb-2'>
                General Inquiries
              </h4>
              <p className='text-muted-foreground mb-2'>
                Email:{' '}
                <a
                  href='mailto:founders@pixelmojo.io'
                  className='text-primary hover:underline'
                >
                  founders@pixelmojo.io
                </a>
              </p>
            </div>
            <div className='p-4 bg-muted/30 border border-line rounded-lg'>
              <h4 className='font-bold text-foreground mb-2'>
                LLM Access Appeals
              </h4>
              <p className='text-muted-foreground mb-2'>
                If you believe your AI system should have access to our content
                for legitimate purposes, please email us with the subject line:
              </p>
              <code className='block p-2 bg-background border border-border rounded text-sm mb-2'>
                LLM Access Appeal
              </code>
              <p className='text-sm text-muted-foreground'>
                Include details about your use case, how you plan to use our
                content, and how you will ensure compliance with this policy.
              </p>
            </div>
            <div className='p-4 bg-muted/30 border border-line rounded-lg'>
              <h4 className='font-bold text-foreground mb-2'>Policy Owner</h4>
              <p className='text-muted-foreground'>
                <strong>Lloyd Pilapil</strong> — Founder & AI Product Architect
              </p>
            </div>
          </div>
        </>
      ),
    },
  ]

  return (
    <div className='container mx-auto px-4 py-16 animate-fade-in'>
      {/* Hero Section */}
      <Hero
        title={<>AI Policy</>}
        description='Terms for AI systems, LLMs, and automated crawlers accessing Pixelmojo content. Last updated: January 1, 2026'
        eyebrow='Legal'
        ctaText='Contact Us'
        ctaHref='/contact-us'
      />

      {/* Quick Navigation */}
      <LenisFadeIn delay={200}>
        <section className='max-w-6xl mx-auto mb-16'>
          <div className='bg-card/50 border border-border p-8'>
            <h2 className='text-xl font-bold mb-4 text-foreground'>
              Quick Navigation
            </h2>
            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-3'>
              {sections.map((section, i) => {
                const Icon = section.icon
                return (
                  <button
                    key={i}
                    onClick={() => toggleSection(i)}
                    className='flex items-center gap-3 p-3 bg-muted/30 hover:bg-primary/10 border border-border hover:border-primary/50 transition-all text-left group'
                  >
                    <Icon className='h-5 w-5 text-primary' />
                    <span className='text-sm font-medium group-hover:text-primary transition-colors'>
                      {section.title}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </section>
      </LenisFadeIn>

      {/* Accordion Sections */}
      <LenisStaggered delay={300} staggerDelay={100}>
        <section className='max-w-4xl mx-auto space-y-4'>
          {sections.map((section, index) => {
            const Icon = section.icon
            const isOpen = openSections.includes(index)
            return (
              <div
                key={index}
                className='bg-card/50 border border-border overflow-hidden transition-all'
              >
                <button
                  onClick={() => toggleSection(index)}
                  className='w-full flex items-center justify-between p-6 text-left hover:bg-muted/30 transition-colors'
                >
                  <div className='flex items-center gap-4'>
                    <Icon className='h-6 w-6 text-primary flex-shrink-0' />
                    <h3 className='text-xl font-bold text-foreground'>
                      {section.title}
                    </h3>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-muted-foreground transition-transform flex-shrink-0 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className='px-6 pb-6 text-muted-foreground'>
                    {section.content}
                  </div>
                )}
              </div>
            )
          })}
        </section>
      </LenisStaggered>

      {/* Related Links */}
      <LenisFadeIn delay={500}>
        <section className='max-w-4xl mx-auto mt-16 pt-8 border-t border-border'>
          <h2 className='text-xl font-bold mb-4 text-foreground'>
            Related Policies
          </h2>
          <div className='grid md:grid-cols-2 gap-4'>
            <Link
              href='/privacy-policy'
              className='p-4 bg-muted/30 border border-border hover:border-primary/50 transition-all rounded-lg'
            >
              <h3 className='font-bold text-foreground mb-2'>Privacy Policy</h3>
              <p className='text-sm text-muted-foreground'>
                How we collect, use, and protect your personal information
              </p>
            </Link>
            <Link
              href='/terms-of-service'
              className='p-4 bg-muted/30 border border-border hover:border-primary/50 transition-all rounded-lg'
            >
              <h3 className='font-bold text-foreground mb-2'>
                Terms of Service
              </h3>
              <p className='text-sm text-muted-foreground'>
                Terms and conditions for using our website and services
              </p>
            </Link>
          </div>
        </section>
      </LenisFadeIn>
    </div>
  )
}
